import moment from 'moment';
import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Modules, useLoggedInUserPermissions } from '~/components/Auth';
import { ListWidgetComponentFactory } from '~/components/MyWorkspace/ListWidget';
import { WithPerspectives } from '~/components/MyWorkspace/ManagePerspectives';
import { DevelopmentTabs } from '~/data/tabs';
import { People } from '~/services/Http';
import { useGetTeamTrainings } from '~/services/Http/hooks';

import { WidgetProps } from '../../data/widgets';

import { Training } from './components/Training';

export interface Props extends WidgetProps<WithPerspectives> {}

const TeamTrainingsWidgetComponent = ListWidgetComponentFactory({
  ListItemComponent: Training,
  getIdentifier: (item: People.TrainingDto) =>
    `${item.key.id}-${item.key.kind}`,
  dataCy: 'team-trainings-widget',
});

export const TeamTrainingsWidget: React.FC<Props> = ({ title, config }) => {
  const { t } = useTranslation();

  const getTeamTrainingsReqParams = {
    employeeId: 'me',
    roles: config?.role ? [config.role] : undefined,
  };
  const {
    trainings: teamTrainings,
    loading,
    failedToLoad,
  } = useGetTeamTrainings(getTeamTrainingsReqParams);

  const permissions = useLoggedInUserPermissions();
  const developmentModuleEnabledTabs =
    permissions.enabledTabs[Modules.DEVELOPMENT];

  const trainings = useMemo(() => {
    if (!Array.isArray(teamTrainings)) {
      return teamTrainings;
    }

    const today = moment();

    return teamTrainings.filter(
      (tt) =>
        today.isSameOrBefore(tt.startDate, 'day') ||
        (tt.endDate && today.isBetween(tt.startDate, tt.endDate, 'day', '[]')),
    );
  }, [teamTrainings]);

  return (
    <TeamTrainingsWidgetComponent
      title={title || t('teamTrainings')}
      data={trainings}
      loading={loading}
      failedToLoad={failedToLoad}
      noDataMessage={t('noTrainings')}
      isTabDisabled={
        !!developmentModuleEnabledTabs &&
        !developmentModuleEnabledTabs.includes(DevelopmentTabs.EDUCATION)
      }
    />
  );
};
